import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

/**
 * Sistema de Toast moderno usando react-hot-toast
 * Compatível com React 19 e integra com notifications do backend
 * 
 * Uso básico:
 * import Toast from '../ui/toast';
 * 
 * Toast.success('Operação realizada com sucesso!');
 * Toast.error('Erro ao realizar operação');
 * Toast.warning('Atenção: verifique os dados');
 * Toast.info('Informação importante');
 * Toast.loading('Processando...');
 * 
 * // Shortcuts para operações comuns
 * Toast.userCreated('João');
 * Toast.passwordChanged();
 * Toast.processBackendNotifications(errorResponse);
 */

// Configuração personalizada para o toast
const toastConfig = {
  duration: 4000,
  position: 'bottom-right',
  style: {
    borderRadius: '12px',
    background: '#fff',
    color: '#333',
    fontSize: '15px',
    fontFamily: 'inherit',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e1e5e9',
    maxWidth: '450px',
    minWidth: '320px',
    padding: '16px 20px',
    lineHeight: '1.4',
  },
  success: {
    iconTheme: {
      primary: '#52c41a',
      secondary: '#fff',
    },
    style: {
      border: '1px solid #b7eb8f',
      background: '#f6ffed',
    },
  },
  error: {
    iconTheme: {
      primary: '#ff4d4f',
      secondary: '#fff',
    },
    style: {
      border: '1px solid #ffccc7',
      background: '#fff2f0',
    },
  },
  loading: {
    iconTheme: {
      primary: '#1890ff',
      secondary: '#fff',
    },
  },
};

const Toast = {
  // Success toast
  success: (message, options = {}) => {
    return toast.success(message, {
      ...toastConfig,
      ...toastConfig.success,
      ...options,
    });
  },

  // Error toast
  error: (message, options = {}) => {
    return toast.error(message, {
      ...toastConfig,
      ...toastConfig.error,
      ...options,
    });
  },

  // Warning toast
  warning: (message, options = {}) => {
    return toast((t) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
        <span style={{ fontSize: '15px', lineHeight: '1.4' }}>{message}</span>
      </div>
    ), {
      ...toastConfig,
      style: {
        ...toastConfig.style,
        border: '1px solid #ffe58f',
        background: '#fffbe6',
      },
      ...options,
    });
  },

  // Info toast
  info: (message, options = {}) => {
    return toast((t) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
        <span style={{ fontSize: '15px', lineHeight: '1.4' }}>{message}</span>
      </div>
    ), {
      ...toastConfig,
      style: {
        ...toastConfig.style,
        border: '1px solid #91d5ff',
        background: '#e6f7ff',
      },
      ...options,
    });
  },

  // Loading toast
  loading: (message = 'Carregando...', options = {}) => {
    return toast.loading(message, {
      ...toastConfig,
      ...toastConfig.loading,
      ...options,
    });
  },

  // Dismiss toast
  dismiss: (toastId) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },

  // Processar notifications do backend automaticamente
  processBackendNotifications: (errorResponse) => {
    const notifications = errorResponse?.response?.data?.notifications || 
                         errorResponse?.data?.notifications;
    
    if (notifications && Array.isArray(notifications) && notifications.length > 0) {
      notifications.forEach((notif, index) => {
        if (notif.message) {
          // Delay pequeno entre múltiplas mensagens para evitar sobreposição
          setTimeout(() => {
            Toast.error(notif.message, {
              id: `backend-notification-${notif.identifier || Date.now()}-${index}`,
              duration: 6000, // Duração maior para erros do backend
            });
          }, index * 200);
        }
      });
      return true;
    }
    return false;
  },

  // Shortcuts para operações comuns
  userCreated: (userName) => {
    return Toast.success(`Usuário ${userName} criado com sucesso!`);
  },

  userUpdated: (userName) => {
    return Toast.success(`Usuário ${userName} atualizado com sucesso!`);
  },

  userDeleted: (userName) => {
    return Toast.success(`Usuário ${userName} removido com sucesso!`);
  },

  passwordChanged: () => {
    return Toast.success('Senha alterada com sucesso!');
  },

  operationFailed: (operation = 'operação') => {
    return Toast.error(`Erro ao realizar ${operation}.`);
  },

  requiredFields: () => {
    return Toast.warning('Preencha todos os campos obrigatórios.');
  },

  noPermission: (action = 'realizar esta ação') => {
    return Toast.error(`Você não tem permissão para ${action}.`);
  },

  // Promise toast - útil para operações assíncronas
  promise: (promise, messages) => {
    return toast.promise(promise, {
      loading: messages.loading || 'Processando...',
      success: messages.success || 'Operação realizada com sucesso!',
      error: messages.error || 'Erro ao realizar operação.',
    }, toastConfig);
  },

  // Custom toast
  custom: (content, options = {}) => {
    return toast.custom(content, {
      ...toastConfig,
      ...options,
    });
  },
};

// Componente do Toaster que deve ser adicionado ao App
export const ToastContainer = () => (
  <Toaster
    position="bottom-right"
    reverseOrder={false}
    gutter={12}
    containerClassName=""
    containerStyle={{
      bottom: 20, // Espaço da parte inferior
      right: 20, // Espaço da direita
      zIndex: 9999,
    }}
    toastOptions={{
      ...toastConfig,
      className: '',
      ariaProps: {
        role: 'status',
        'aria-live': 'polite',
      },
    }}
  />
);

export default Toast; 